import { HStack, Stack, VStack } from '@chakra-ui/react';
import { Button, ExternalLink, FormField, Input } from 'Atoms';
import { Typography } from 'Tokens';
import { Logo } from 'Molecules';
import React, { useEffect, useState } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';
import PasswordStrengthBar from 'react-password-strength-bar';
import { useToast } from 'utils/hooks';
import { useTranslation } from 'utils/translation';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';

const MIN_PASSWORD_LENGTH = import.meta.env.REACT_APP_MIN_PASSWORD_LENGTH || 8;

type UserFields = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};
export const SignUpForm = ({
  onSuccess,
  email,
  invitationId,
}: {
  onSuccess?: () => void;
  email?: string;
  invitationId?: string;
}) => {
  const [, setErrorMessage] = React.useState<string>();

  const nhost = useNhostClient();
  const toast = useToast();
  const { t } = useTranslation(['login', 'common']);
  const { control, handleSubmit, watch, resetField } = useForm<UserFields>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: email ?? '',
      password: '',
    },
  });

  const { errors, isSubmitting } = useFormState({ control });

  useEffect(() => {
    if (email) resetField('email', { defaultValue: email });
  }, [email]);

  const [passwordStrength, setPasswordStrength] = useState<number>(0);

  const onSubmit = async (data: UserFields) => {
    if (!invitationId) {
      toast({
        text: t('login:toast.invalidInvitation.title'),
        variant: 'danger',
      });
      return;
    }

    try {
      const res = await nhost.functions.post('/create-user', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        invitationId,
      });

      if (res.body !== null) {
        const res = await nhost.auth.signInEmailPassword({
          email: data.email,
          password: data.password,
        });
        if (res.body.session) {
          if (onSuccess) onSuccess();
        }
      }
    } catch (error: any) {
      setErrorMessage(error?.message);
      toast({
        text: error.message,
        variant: 'danger',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing="16px" width="320px">
        <HStack spacing="8px" alignItems="flex-start">
          <Controller
            name="firstName"
            control={control}
            rules={{ required: t('login:fields.firstName.required') }}
            render={({ field }) => (
              <FormField
                id="firstName"
                label={t('login:form.firstName')}
                isRequired
                isInvalid={!!errors.firstName}
                error={errors.firstName?.message ?? ''}
              >
                <Input
                  width="100%"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder={t('login:form.placeholder.firstName')}
                  autoComplete="given-name"
                  onBlur={field.onBlur}
                />
              </FormField>
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{ required: t('login:fields.lastName.required') }}
            render={({ field }) => (
              <FormField
                id="lastName"
                label={t('login:form.lastName')}
                isRequired
                isInvalid={!!errors.lastName}
                error={errors.lastName?.message ?? ''}
              >
                <Input
                  width="100%"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder={t('login:form.placeholder.lastName')}
                  autoComplete="family-name"
                  onBlur={field.onBlur}
                />
              </FormField>
            )}
          />
        </HStack>
        <FormField
          id="email"
          label={t('common:fields.email.label')}
          isRequired
          isInvalid={!!errors.email}
          error={t('login:fields.email.businessEmail')}
        >
          <Input
            value={email}
            placeholder={t('common:fields.email.label')}
            autoComplete="email"
            width="100%"
            isDisabled
          />
        </FormField>

        <Controller
          name="password"
          control={control}
          rules={{
            required: true,
            validate: (_v) => passwordStrength >= 2 && watch('password').length >= 8,
          }}
          render={({ field }) => {
            return (
              <FormField
                id="password"
                label={t('login:fields.password.label')}
                isRequired
                isInvalid={!!errors.password || field.value.length < MIN_PASSWORD_LENGTH}
                error={passwordStrength < 2 ? t('login:fields.password.strongerRequired') : ''}
              >
                <VStack spacing="2px" width="100%" alignItems="stretch">
                  <PasswordStrengthBar
                    password={field.value}
                    onChangeScore={(score) => setPasswordStrength(score)}
                    scoreWords={[
                      t('common:fields.passwordStrength.weak'),
                      t('common:fields.passwordStrength.weak'),
                      t('common:fields.passwordStrength.okay'),
                      t('common:fields.passwordStrength.good'),
                      t('common:fields.passwordStrength.strong'),
                    ]}
                    shortScoreWord={
                      !!field.value ? t('common:fields.passwordStrength.tooShort') : ''
                    }
                    style={{
                      display: 'flex',
                      flexDirection: 'column-reverse',
                      height: '2px',
                      overflow: 'visible',
                      marginBottom: '4px',
                    }}
                    userInputs={watch(['firstName', 'lastName', 'email'])}
                    minLength={8}
                  />
                  <Input
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder={t('login:fields.password.label')}
                    type="password"
                    autoComplete="new-password"
                    width="100%"
                    onBlur={field.onBlur}
                  />
                </VStack>
              </FormField>
            );
          }}
        />
        <Button type="submit" width="full" variant="primary" mt="24px" isLoading={isSubmitting}>
          {email ? t('common:invitations.acceptButton') : t('login:signUp')}
        </Button>
      </VStack>
    </form>
  );
};
export const SignUpPageWrapper = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation(['login', 'common']);
  return (
    <Stack minHeight="100vh" gap="0px" p="10%" background="bg.decorative">
      <VStack spacing="0px">
        <Logo size="110px" />
        <Typography variant="bodyStrong">{t('login:createAccount')}</Typography>
        <Typography variant="detail" mt="2px">
          {t('login:haveAccount')}{' '}
          <ExternalLink fontSize="detail" href="/login" style={{ color: 'text.action' }}>
            {t('login:logIn')}
          </ExternalLink>
        </Typography>
        <VStack width="100%" mt="56px">
          {children}
        </VStack>
        <HStack width="320px">
          <Typography variant="detail" textAlign="center" mt="16px">
            {t('login:confirmation.text')}{' '}
            <ExternalLink
              fontSize="detail"
              lineHeight="s"
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.celsia.io/terms-of-use"
            >
              {t('login:confirmation.terms')}
            </ExternalLink>{' '}
            {t('common:words.and')}{' '}
            <ExternalLink
              fontSize="detail"
              lineHeight="s"
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.celsia.io/privacy-policy"
            >
              {t('login:confirmation.policy')}
            </ExternalLink>
          </Typography>
        </HStack>
      </VStack>
    </Stack>
  );
};

export const SignUp = () => {
  return (
    <SignUpPageWrapper>
      <SignUpForm />
    </SignUpPageWrapper>
  );
};
